/**
 * UPT-2079 L12a (spec web `SPEC-UPT-2079-L12.md` §4, « Démonstration ») — « socle d'abord » et « noms déjà pris » en LOCAL pour le dépôt de démonstration : fonction
 * pure, sans IA ni réseau, qui reproduit de façon COMPACTE sur une COPIE du document les corrections que le serveur fait avant de proposer une version
 * (rest `SPEC-UPT-2079-L12.md` §4.2 et §4.3) — de quoi voir, sans serveur, les quatre types d'exemple de la démonstration :
 *   - `core.entity_removed`  : un objet qui porte le nom d'une capacité du socle (pièces jointes, commentaires, historique, tâches…) ET n'a que des champs génériques
 *                              de cette capacité est retiré ;
 *   - `core.relation_removed`: les liens dont il était une extrémité sont retirés (l'autre extrémité déclare la capacité) ;
 *   - `core.field_retyped`   : un champ qui pointait l'objet retiré devient `attachments` (autre capacité : `core.field_removed`) ;
 *   - `core.reuse_declared`, `core.reference_cleaned` : la capacité est déclarée (`coreReuse`) pour l'objet concerné ; menus, écrans, widgets et données d'exemple qui
 *                              citaient l'objet retiré sont nettoyés ;
 *   - `entity.renamed`       : un objet dont le code est déjà pris par la plateforme (`auditevent`, `todo`…) et qui n'est pas retirable est renommé `<module><code>`.
 * Chaque correction est rendue en `ModuleSpecRepairChange` : chemin de l'ÉLÉMENT dans le document REÇU (avant correction), type, avant, après.
 *
 * APPROXIMATION DE DÉMONSTRATION : le serveur reste la référence (liste complète des capacités, lecture réelle de l'espace de noms de la base, sources machine seulement,
 * propagation des renommages à tous les emplacements du schéma). Prudence identique : en cas de doute, l'objet reste. Idempotent ; ne lance jamais.
 */
import type { ModuleSpecDocument, ModuleSpecRepairChange } from '../data/moduleSpecContracts';

type Json = Record<string, unknown>;

const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

interface CoreCapability {
  /** Nom de la capacité (`coreReuse[].capability`). */
  name: string;
  /** Noms repliés d'un objet qui la recouvre. */
  stems: readonly string[];
  /** Codes d'entité du socle qui la portent. */
  coreCodes: readonly string[];
  /** Noms (repliés) des champs génériques de la capacité. */
  fields: readonly string[];
}

/** Rest §4.2, dans l'ordre de départage : `attachments, comments, history, tasks, tags, risks, goals, macroplans, information`. */
const CAPABILITIES: readonly CoreCapability[] = [
  {
    name: 'attachments',
    stems: ['attachment', 'piecejointe', 'fichier', 'file'],
    coreCodes: [],
    fields: ['name', 'filename', 'title', 'description', 'file', 'url', 'path', 'mimetype', 'contenttype', 'size', 'sizebytes', 'hash', 'checksum', 'uploadedby', 'uploadedat', 'kind', 'type', 'category', 'version'],
  },
  {
    name: 'comments',
    stems: ['comment', 'commentaire'],
    coreCodes: [],
    fields: ['text', 'body', 'content', 'message', 'comment', 'author', 'authorid', 'visibility', 'mentions', 'parent', 'parentid'],
  },
  {
    name: 'history',
    stems: ['auditevent', 'auditlog', 'audittrail', 'audit', 'history', 'historique', 'changelog', 'activitylog'],
    coreCodes: ['auditevent'],
    fields: ['action', 'event', 'eventtype', 'timestamp', 'at', 'date', 'actor', 'user', 'userid', 'username', 'details', 'description', 'reason', 'before', 'after', 'oldvalue', 'newvalue', 'field', 'fieldname', 'changes', 'diff', 'source', 'ip'],
  },
  {
    name: 'tasks',
    stems: ['task', 'tache', 'todo'],
    coreCodes: ['todo'],
    fields: ['title', 'name', 'description', 'status', 'assignee', 'assignedto', 'owner', 'duedate', 'priority', 'done', 'completedat'],
  },
  { name: 'tags', stems: ['tag', 'etiquette'], coreCodes: ['tag'], fields: ['name', 'label', 'color', 'description'] },
  {
    name: 'risks',
    stems: ['risk', 'risque'],
    coreCodes: ['risk'],
    fields: ['title', 'name', 'description', 'probability', 'impact', 'severity', 'score', 'owner', 'mitigation', 'status'],
  },
  {
    name: 'goals',
    stems: ['goal', 'objectif'],
    coreCodes: ['goal'],
    fields: ['title', 'name', 'description', 'target', 'targetvalue', 'currentvalue', 'unit', 'duedate', 'owner', 'status', 'progress'],
  },
  {
    name: 'macroplans',
    stems: ['macroplan'],
    coreCodes: ['macroplan'],
    fields: ['title', 'name', 'description', 'startdate', 'enddate', 'owner', 'status'],
  },
  { name: 'information', stems: ['information'], coreCodes: ['information'], fields: ['title', 'name', 'message', 'level', 'startdate', 'enddate'] },
];

/** Champs de plate-forme toujours admis. */
const PLATFORM_FIELDS: readonly string[] = ['id', 'uuid', 'tenantid', 'workspaceid', 'createdat', 'updatedat', 'createdby', 'updatedby'];
/** Capacités qui valent pour tout le module (l'historique de toutes les modifications). */
const TRANSVERSAL_CAPABILITIES: readonly string[] = ['history'];
/** Un objet générique a au plus ce nombre de champs. */
const MAX_GENERIC_FIELDS = 12;
/** Types de champ qui désignent un autre objet. */
const REFERENCE_TYPES: readonly string[] = ['typeahead', 'entityref', 'references'];
/** Codes d'entité déjà pris par la plateforme en démonstration (le serveur lit la base). */
export const PLATFORM_ENTITY_CODES: ReadonlySet<string> = new Set(CAPABILITIES.flatMap((capability) => capability.coreCodes));
/** Borne du code d'entité (`^[a-z][a-z0-9]{0,40}$`) et du préfixe de module. */
const ENTITY_CODE_MAX = 41;
const PREFIX_MAX = 12;

const NOTE_PREFIX = 'Déclaré automatiquement : l\'objet « ';
const NOTE_SUFFIX = ' » du prototype recouvrait cette capacité du socle.';

const ascii = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '');
/** Nom replié : minuscules, sans accents ni ponctuation, pluriel `s` final retiré. */
const fold = (value: string): string => ascii(value).toLowerCase().replace(/[^a-z0-9]/g, '').replace(/s$/, '');

const labelOf = (value: unknown, language: string): string => {
  const record = asRecord(value);
  return record ? asString(record[language]) : typeof value === 'string' ? value : '';
};

export interface LocalCoreFirstOptions {
  /** Index d'un lien dans le document REÇU, d'après son index dans le document lu (les corrections mécaniques ont pu en retirer). Identité par défaut. */
  relationIndex?: (index: number) => number;
}

/** La capacité que l'objet `code` recouvre par son nom (code, libellés fr / en, préfixe du module) ou par son code du socle ; `null` s'il n'en recouvre aucune. */
const candidateOf = (code: string, entity: Json, moduleFold: string): CoreCapability | null => {
  const label = asRecord(entity.label);
  const names = [fold(code), fold(labelOf(label?.singular, 'fr')), fold(labelOf(label?.singular, 'en'))].filter((name) => name !== '');
  return (
    CAPABILITIES.find(
      (capability) =>
        capability.coreCodes.includes(code) ||
        names.some((name) => capability.stems.includes(name) || (moduleFold !== '' && capability.stems.some((stem) => name === `${moduleFold}${stem}`)))
    ) ?? null
  );
};

/** Un champ qui désigne un autre objet. */
const referencedEntity = (field: Json): string => {
  const data = asRecord(field.data);
  return asString(data?.entity) || asString(asRecord(data?.store)?.entity);
};

const isReference = (field: Json): boolean => REFERENCE_TYPES.includes(asString(field.type)) || referencedEntity(field) !== '';

/** Un objet est générique (retirable) s'il n'a ni workflow ni règle, au plus 12 champs, tous génériques de la capacité ou des références. */
const isGeneric = (entity: Json, capability: CoreCapability): boolean => {
  if (entity.workflow !== undefined || asArray(entity.rules).length > 0) return false;
  const fields = asArray(entity.fields);
  if (fields.length > MAX_GENERIC_FIELDS) return false;
  return fields.every((raw) => {
    const field = asRecord(raw);
    if (!field) return false;
    const name = fold(asString(field.name));
    return PLATFORM_FIELDS.includes(name) || capability.fields.includes(name) || isReference(field);
  });
};

/** Remplace `from` par `to` dans une chaîne (champ `entity`), ailleurs ne touche à rien. */
const renamed = (value: unknown, from: string, to: string): unknown => (value === from ? to : value);

/**
 * Applique le socle d'abord puis les noms déjà pris sur le document `spec` (copie modifiée en place) ; rend les corrections, chemins du document REÇU.
 */
export function moduleSpecLocalCoreFirst(spec: ModuleSpecDocument, options: LocalCoreFirstOptions = {}): ModuleSpecRepairChange[] {
  const changes: ModuleSpecRepairChange[] = [];
  const entities = asRecord(spec.entities);
  if (!entities) return changes;
  const relationIndex = options.relationIndex ?? ((index: number) => index);
  const module = asRecord(spec.module);
  const moduleCode = asString(module?.code);
  const moduleFold = fold(moduleCode.replace(/-/g, ''));
  const codes = Object.keys(entities);

  // ── 1. Socle d'abord : quels objets partent (le dernier objet du document ne part jamais) ──────────────────────────────────────────────────────────────────
  const removed = new Map<string, CoreCapability>();
  codes.forEach((code) => {
    if (codes.length - removed.size <= 1) return;
    const entity = asRecord(entities[code]);
    const capability = entity ? candidateOf(code, entity, moduleFold) : null;
    if (entity && capability && isGeneric(entity, capability)) removed.set(code, capability);
  });

  // Capacité → objets qui la déclarent → objet retiré à l'origine de la déclaration (le premier gagne : c'est le `before` de la correction).
  const reuse = new Map<string, Map<string, string>>();
  const declare = (capability: CoreCapability, code: string, origin: string): void => {
    if (removed.has(code)) return;
    const owners = reuse.get(capability.name) ?? new Map<string, string>();
    if (!owners.has(code)) owners.set(code, origin);
    reuse.set(capability.name, owners);
  };

  if (removed.size > 0) {
    removed.forEach((capability, code) => {
      changes.push({ path: `/entities/${code}`, kind: 'core.entity_removed', before: code, after: capability.name });
    });

    // Liens : retirés quand l'une des extrémités part ; l'autre extrémité déclare la capacité.
    const relations = asArray(spec.relations);
    const keptRelations: unknown[] = [];
    relations.forEach((raw, index) => {
      const relation = asRecord(raw);
      const source = asString(asRecord(relation?.source)?.entity);
      const target = asString(asRecord(relation?.target)?.entity);
      const gone = removed.get(source) ?? removed.get(target);
      if (!relation || !gone) {
        keptRelations.push(raw);
        return;
      }
      const origin = removed.has(source) ? source : target;
      changes.push({ path: `/relations/${relationIndex(index)}`, kind: 'core.relation_removed', before: asString(relation.code), after: '' });
      declare(gone, source, origin);
      declare(gone, target, origin);
    });
    if (keptRelations.length !== relations.length) spec.relations = keptRelations;

    // Champs et `parent` qui pointaient un objet retiré.
    codes.forEach((code) => {
      const entity = asRecord(entities[code]);
      if (!entity || removed.has(code)) return;
      const parent = asRecord(entity.parent);
      const parentCapability = removed.get(asString(parent?.entity));
      if (parentCapability) {
        changes.push({ path: `/entities/${code}/parent`, kind: 'core.reference_cleaned', before: asString(parent?.entity), after: '' });
        delete entity.parent;
        declare(parentCapability, code, asString(parent?.entity));
      }
      const fields = asArray(entity.fields);
      const keptFields: unknown[] = [];
      fields.forEach((raw, index) => {
        const field = asRecord(raw);
        const capability = field ? removed.get(referencedEntity(field)) : undefined;
        if (!field || !capability) {
          keptFields.push(raw);
          return;
        }
        declare(capability, code, referencedEntity(field));
        if (capability.name === 'attachments') {
          changes.push({ path: `/entities/${code}/fields/${index}`, kind: 'core.field_retyped', before: asString(field.type), after: 'attachments' });
          // Le champ garde son nom, son libellé, `required`, `help` et `doc` ; `data`, `storage`, `unique`, `default`, `validators`, `protection`, `mode` et `grid` partent.
          keptFields.push({
            name: field.name,
            type: 'attachments',
            ...(field.label !== undefined ? { label: field.label } : {}),
            ...(field.required !== undefined ? { required: field.required } : {}),
            ...(field.help !== undefined ? { help: field.help } : {}),
            ...(field.doc !== undefined ? { doc: field.doc } : {}),
          });
        } else {
          changes.push({ path: `/entities/${code}/fields/${index}`, kind: 'core.field_removed', before: asString(field.name), after: '' });
        }
      });
      if (keptFields.length !== fields.length || fields.some((raw, index) => raw !== keptFields[index])) entity.fields = keptFields;
    });

    // Une capacité transversale (l'historique) dont l'objet retiré n'était référencé par personne vaut pour les objets principaux (`kind` absent ou `master`), à défaut
    // pour tous les objets restants : sans cela l'information serait perdue en silence.
    removed.forEach((capability, code) => {
      if (!TRANSVERSAL_CAPABILITIES.includes(capability.name) || [...(reuse.get(capability.name)?.values() ?? [])].includes(code)) return;
      const remaining = codes.filter((candidate) => !removed.has(candidate));
      const main = remaining.filter((candidate) => ['', 'master'].includes(asString(asRecord(entities[candidate])?.kind)));
      (main.length > 0 ? main : remaining).forEach((owner) => declare(capability, owner, code));
    });

    // Déclaration `coreReuse` (fusionnée avec l'existant, sans doublon, objets dans l'ordre du document).
    const coreReuse = asArray(spec.coreReuse).filter((raw): raw is Json => asRecord(raw) !== null);
    reuse.forEach((owners, capabilityName) => {
      const entry = coreReuse.find((item) => asString(item.capability) === capabilityName);
      const listed = new Set(asArray(entry?.entities).filter((value): value is string => typeof value === 'string'));
      const fresh = codes.filter((code) => owners.has(code) && !listed.has(code));
      fresh.forEach((code) => changes.push({ path: `/entities/${code}`, kind: 'core.reuse_declared', before: owners.get(code) ?? '', after: capabilityName }));
      if (fresh.length === 0) return;
      const merged = codes.filter((code) => owners.has(code) || listed.has(code));
      if (entry) {
        entry.entities = merged;
      } else {
        const origin = owners.get(fresh[0]) ?? capabilityName;
        coreReuse.push({ capability: capabilityName, entities: merged, note: `${NOTE_PREFIX}${origin}${NOTE_SUFFIX}` });
      }
    });
    if (coreReuse.length > 0) spec.coreReuse = coreReuse;

    // Références nettoyées : données d'exemple, déclarations, écrans, menus, widgets.
    const demoData = asRecord(spec.demoData);
    removed.forEach((_capability, code) => {
      if (demoData && code in demoData) {
        changes.push({ path: `/demoData/${code}`, kind: 'core.reference_cleaned', before: code, after: '' });
        delete demoData[code];
      }
    });
    asArray(spec.pages).forEach((raw, pageIndex) => {
      const page = asRecord(raw);
      const pageEntities = asArray(page?.entities);
      if (!page) return;
      pageEntities.forEach((value, entityIndex) => {
        if (typeof value === 'string' && removed.has(value)) {
          changes.push({ path: `/pages/${pageIndex}/entities/${entityIndex}`, kind: 'core.reference_cleaned', before: value, after: '' });
        }
      });
      if (Array.isArray(page.entities)) page.entities = pageEntities.filter((value) => !(typeof value === 'string' && removed.has(value)));
      for (const property of ['reads', 'writes']) {
        if (!Array.isArray(page[property])) continue;
        page[property] = (page[property] as unknown[]).filter((value, index) => {
          if (typeof value !== 'string' || !removed.has(value)) return true;
          changes.push({ path: '/pages/' + pageIndex + '/' + property + '/' + index, kind: 'core.reference_cleaned', before: value, after: '' });
          return false;
        });
      }
    });
    const navigation = asRecord(spec.navigation);
    asArray(navigation?.menus).forEach((rawMenu, menuIndex) => {
      const menu = asRecord(rawMenu);
      if (!menu) return;
      const items = asArray(menu.items);
      items.forEach((rawItem, itemIndex) => {
        const entity = asString(asRecord(rawItem)?.entity);
        if (entity !== '' && removed.has(entity)) {
          changes.push({ path: `/navigation/menus/${menuIndex}/items/${itemIndex}`, kind: 'core.reference_cleaned', before: entity, after: '' });
        }
      });
      menu.items = items.filter((rawItem) => !removed.has(asString(asRecord(rawItem)?.entity)));
    });
    if (navigation && removed.has(asString(navigation.homePage))) {
      changes.push({ path: '/navigation/homePage', kind: 'core.reference_cleaned', before: asString(navigation.homePage), after: '' });
      delete navigation.homePage;
    }
    const dashboard = asRecord(spec.dashboard);
    const widgets = asArray(dashboard?.widgets);
    widgets.forEach((rawWidget, widgetIndex) => {
      const entity = asString(asRecord(rawWidget)?.entity);
      if (entity !== '' && removed.has(entity)) {
        changes.push({ path: `/dashboard/widgets/${widgetIndex}`, kind: 'core.reference_cleaned', before: entity, after: '' });
      }
    });
    if (dashboard && widgets.some((rawWidget) => removed.has(asString(asRecord(rawWidget)?.entity)))) {
      dashboard.widgets = widgets.filter((rawWidget) => !removed.has(asString(asRecord(rawWidget)?.entity)));
    }
    removed.forEach((_capability, code) => {
      delete entities[code];
    });
  }

  // ── 2. Noms déjà pris : un objet non retirable dont le code appartient à la plateforme est renommé `<module><code>` ───────────────────────────────────────────
  const prefix = moduleCode.replace(/-/g, '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, PREFIX_MAX) || 'mod';
  const survivors = Object.keys(entities);
  survivors.forEach((code) => {
    if (!PLATFORM_ENTITY_CODES.has(code)) return;
    let candidate = `${prefix}${code}`.slice(0, ENTITY_CODE_MAX);
    for (let suffix = 2; suffix <= 9 && candidate in entities; suffix += 1) candidate = `${prefix}${code}${suffix}`.slice(0, ENTITY_CODE_MAX);
    if (candidate in entities) return; // aucun candidat libre : l'objet reste (le contrôle de livraison le dira)
    changes.push({ path: `/entities/${code}`, kind: 'entity.renamed', before: code, after: candidate });
    renameEntity(spec, code, candidate);
  });

  return changes;
}

/** Renomme l'objet `from` en `to` à tous les emplacements courants du document (clé de `entities` en gardant l'ordre, liens, déclarations, écrans, menus, widgets, champs). */
function renameEntity(spec: ModuleSpecDocument, from: string, to: string): void {
  const entities = asRecord(spec.entities);
  if (entities) {
    const entries = Object.entries(entities).map(([key, value]) => [key === from ? to : key, value] as const);
    Object.keys(entities).forEach((key) => delete entities[key]);
    entries.forEach(([key, value]) => {
      entities[key] = value;
    });
    Object.values(entities).forEach((rawEntity) => {
      const entity = asRecord(rawEntity);
      if (!entity) return;
      const parent = asRecord(entity.parent);
      if (parent) parent.entity = renamed(parent.entity, from, to);
      asArray(entity.fields).forEach((rawField) => {
        const data = asRecord(asRecord(rawField)?.data);
        if (!data) return;
        data.entity = renamed(data.entity, from, to);
        const store = asRecord(data.store);
        if (store) store.entity = renamed(store.entity, from, to);
      });
    });
  }
  const demoData = asRecord(spec.demoData);
  if (demoData && from in demoData) {
    demoData[to] = demoData[from];
    delete demoData[from];
  }
  asArray(spec.relations).forEach((rawRelation) => {
    const relation = asRecord(rawRelation);
    ['source', 'target'].forEach((end) => {
      const side = asRecord(relation?.[end]);
      if (side) side.entity = renamed(side.entity, from, to);
    });
  });
  asArray(spec.coreReuse).forEach((rawEntry) => {
    const entry = asRecord(rawEntry);
    if (entry) entry.entities = asArray(entry.entities).map((value) => renamed(value, from, to));
  });
  asArray(spec.pages).forEach((rawPage) => {
    const page = asRecord(rawPage);
    if (page) for (const property of ['entities', 'reads', 'writes']) {
      if (Array.isArray(page[property])) page[property] = (page[property] as unknown[]).map((value) => renamed(value, from, to));
    }
  });
  const navigation = asRecord(spec.navigation);
  asArray(navigation?.menus).forEach((rawMenu) => {
    asArray(asRecord(rawMenu)?.items).forEach((rawItem) => {
      const item = asRecord(rawItem);
      if (item && 'entity' in item) item.entity = renamed(item.entity, from, to);
    });
  });
  if (navigation && 'homePage' in navigation) navigation.homePage = renamed(navigation.homePage, from, to);
  asArray(asRecord(spec.dashboard)?.widgets).forEach((rawWidget) => {
    const widget = asRecord(rawWidget);
    if (widget && 'entity' in widget) widget.entity = renamed(widget.entity, from, to);
  });
}
